// // screens/marketplace/MarketplaceCampaigns.jsx
// import React, { useCallback, useEffect, useMemo, useState } from "react";
// import {
//   CalendarDays,
//   Plus,
//   X,
//   Clock,
//   Trash2,
//   Package,
//   ChevronDown,
//   ChevronUp,
//   AlertCircle,
//   RefreshCw,
//   Layers,
//   Pencil,
//   Save,
// } from "lucide-react";
// import { useAuth } from "../../providers/AuthProvider";
// import { useToast } from "../../providers/ToastProvider";
// import {
//   PageShell,
//   Section,
//   Button,
//   Field,
//   Input,
//   Textarea,
//   Select,
//   EmptyState,
//   LoadingBlock,
//   parseApiError,
//   useTokens,
// } from "./shared";

// /* ══════════════════════════════════════════════════════════
//    Campaign API contract (see docs):
//    POST   /marketplace/pre-orders/              → create with product_prices[]
//    PATCH  /marketplace/pre-orders/{id}/         → merge:
//        • item with `id`          → update existing PreOrderProduct
//        • item with `product_id`  → add new product
//        • omitted products        → untouched
//    DELETE /marketplace/pre-orders/{id}/         → close (only while end_date > now)

//    Rules:
//    • product_prices must contain ≥1 item on CREATE
//    • start_date < end_date, start must be in the future on CREATE
//    • product.sale_type must be PRE_ORDER or BOTH
//    • price_customer, price_agent are decimals (no comparison rule)
//    • stock_limit optional; 0 = unlimited
//    • PATCH/DELETE rejected if end_date <= now
//    ══════════════════════════════════════════════════════════ */

// const emptyLine = () => ({
//   key: Math.random().toString(36).slice(2),
//   pre_order_product_id: null, // null → new; set → existing row
//   product_id: "",
//   price_customer: "",
//   price_agent: "",
//   stock_limit: "",
//   sold_count: 0,
//   product_name: "",
// });

// const emptyForm = () => ({
//   name: "",
//   description: "",
//   start_date: "",
//   end_date: "",
//   is_active: true,
// });

// const toLocalInput = (iso) => {
//   if (!iso) return "";
//   const d = new Date(iso);
//   if (isNaN(d)) return "";
//   const pad = (n) => String(n).padStart(2, "0");
//   return (
//     d.getFullYear() +
//     "-" +
//     pad(d.getMonth() + 1) +
//     "-" +
//     pad(d.getDate()) +
//     "T" +
//     pad(d.getHours()) +
//     ":" +
//     pad(d.getMinutes())
//   );
// };

// const fmtDateTime = (iso) => {
//   if (!iso) return "—";
//   try {
//     return new Date(iso).toLocaleString(undefined, {
//       month: "short",
//       day: "numeric",
//       hour: "2-digit",
//       minute: "2-digit",
//     });
//   } catch {
//     return "—";
//   }
// };

// const campaignPhase = (c) => {
//   const now = Date.now();
//   const start = new Date(c.start_date).getTime();
//   const end = new Date(c.end_date).getTime();
//   if (!c.is_active) return "closed";
//   if (now < start) return "scheduled";
//   if (now > end) return "ended";
//   return "open";
// };

// export default function MarketplaceCampaigns() {
//   const { authFetch } = useAuth();
//   const { showToast } = useToast();
//   const t = useTokens();

//   const [campaigns, setCampaigns] = useState([]);
//   const [products, setProducts] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [refreshing, setRefreshing] = useState(false);

//   // Editor state — shared between create and edit
//   const [editor, setEditor] = useState(null);
//   // { mode: "create" | "edit", campaignId?, form, lines, submitting, errors }

//   const [expanded, setExpanded] = useState({});

//   // ── Load ────────────────────────────────────────────────
//   const load = useCallback(
//     async ({ silent } = {}) => {
//       if (!silent) setLoading(true);
//       else setRefreshing(true);
//       try {
//         const [cRes, pRes] = await Promise.all([
//           authFetch("/marketplace/pre-orders/"),
//           authFetch("/marketplace/products/"),
//         ]);
//         const [cData, pData] = await Promise.all([cRes.json(), pRes.json()]);
//         if (!cRes.ok)
//           throw new Error(parseApiError(cData, "Could not load campaigns"));
//         if (!pRes.ok)
//           throw new Error(parseApiError(pData, "Could not load products"));
//         setCampaigns(cData.data || []);
//         setProducts(pData.data || []);
//       } catch (err) {
//         showToast(err.message, "error");
//       } finally {
//         setLoading(false);
//         setRefreshing(false);
//       }
//     },
//     [authFetch, showToast]
//   );

//   useEffect(() => {
//     load();
//   }, [load]);

//   // ── Derived ─────────────────────────────────────────────
//   const eligible = useMemo(
//     () =>
//       products.filter(
//         (p) => p.sale_type === "PRE_ORDER" || p.sale_type === "BOTH"
//       ),
//     [products]
//   );

//   const productMap = useMemo(() => {
//     const m = new Map();
//     products.forEach((p) => m.set(p.id, p));
//     return m;
//   }, [products]);

//   // ── Open editor: create ────────────────────────────────
//   const openCreate = () => {
//     setEditor({
//       mode: "create",
//       campaignId: null,
//       form: emptyForm(),
//       lines: [emptyLine()],
//       submitting: false,
//       errors: {},
//     });
//   };

//   // ── Open editor: edit ──────────────────────────────────
//   const openEdit = (campaign) => {
//     const items = campaign.products || campaign.pre_order_products || [];
//     setEditor({
//       mode: "edit",
//       campaignId: campaign.id,
//       form: {
//         name: campaign.name || "",
//         description: campaign.description || "",
//         start_date: toLocalInput(campaign.start_date),
//         end_date: toLocalInput(campaign.end_date),
//         is_active: !!campaign.is_active,
//       },
//       // Existing rows pre-populated; a new blank line appended for convenience
//       lines: [
//         ...items.map((it) => ({
//           key: Math.random().toString(36).slice(2),
//           pre_order_product_id: it.id,
//           product_id: it.product,
//           product_name: it.product_name,
//           price_customer: String(it.price_customer || ""),
//           price_agent: String(it.price_agent || ""),
//           stock_limit:
//             it.stock_limit === 0 || it.stock_limit == null
//               ? ""
//               : String(it.stock_limit),
//           sold_count: it.sold_count || 0,
//         })),
//       ],
//       submitting: false,
//       errors: {},
//     });
//   };

//   const closeEditor = () => setEditor(null);

//   // ── Line mutations ─────────────────────────────────────
//   const addLine = () =>
//     setEditor((ed) =>
//       ed ? { ...ed, lines: [...ed.lines, emptyLine()] } : ed
//     );

//   const removeLine = (key) =>
//     setEditor((ed) =>
//       ed ? { ...ed, lines: ed.lines.filter((l) => l.key !== key) } : ed
//     );

//   const updateLine = (key, patch) =>
//     setEditor((ed) =>
//       ed
//         ? {
//             ...ed,
//             lines: ed.lines.map((l) => (l.key === key ? { ...l, ...patch } : l)),
//           }
//         : ed
//     );

//   // ── Validation ─────────────────────────────────────────
//   const validate = (ed) => {
//     const errs = {};
//     if (!ed.form.name.trim()) errs.name = "Name is required";

//     const start = ed.form.start_date ? new Date(ed.form.start_date) : null;
//     const end = ed.form.end_date ? new Date(ed.form.end_date) : null;

//     if (!start) errs.start_date = "Start is required";
//     else if (ed.mode === "create" && start.getTime() < Date.now() - 60_000)
//       errs.start_date = "Start cannot be in the past";
//     if (!end) errs.end_date = "End is required";
//     else if (start && end.getTime() <= start.getTime())
//       errs.end_date = "End must be after start";

//     const lineErrors = {};
//     const seenProductIds = new Set();
//     // Only enforce "at least one new/priced line" for create
//     const validLines = ed.lines.filter(
//       (l) => l.product_id && l.price_customer && l.price_agent
//     );
//     if (ed.mode === "create" && validLines.length === 0) {
//       errs._lines = { _general: "Add at least one product with prices." };
//     }

//     ed.lines.forEach((l) => {
//       const lerr = {};
//       const hasAny =
//         l.product_id || l.price_customer || l.price_agent || l.stock_limit;
//       if (!hasAny) return; // silently skip empty rows in edit mode

//       if (!l.product_id) lerr.product_id = "Pick a product";
//       else if (seenProductIds.has(l.product_id))
//         lerr.product_id = "Already in this campaign";
//       else seenProductIds.add(l.product_id);

//       const c = parseFloat(l.price_customer);
//       const a = parseFloat(l.price_agent);
//       if (!l.price_customer || isNaN(c) || c < 0)
//         lerr.price_customer = "Customer price required";
//       if (!l.price_agent || isNaN(a) || a < 0)
//         lerr.price_agent = "Agent price required";

//       if (l.stock_limit !== "") {
//         const n = Number(l.stock_limit);
//         if (isNaN(n) || n < 0) lerr.stock_limit = "Invalid stock limit";
//       }

//       if (Object.keys(lerr).length) lineErrors[l.key] = lerr;
//     });

//     if (Object.keys(lineErrors).length) errs._lines = lineErrors;
//     return errs;
//   };

//   // ── Submit (create or edit) ────────────────────────────
//   const submitEditor = async () => {
//     if (!editor) return;
//     const errs = validate(editor);
//     if (Object.keys(errs).length) {
//       setEditor((ed) => ({ ...ed, errors: errs }));
//       showToast("Please fix the highlighted fields", "error");
//       return;
//     }

//     setEditor((ed) => ({ ...ed, submitting: true, errors: {} }));

//     const form = editor.form;
//     const payload = {
//       name: form.name.trim(),
//       description: form.description.trim(),
//       start_date: new Date(form.start_date).toISOString(),
//       end_date: new Date(form.end_date).toISOString(),
//       is_active: form.is_active,
//     };

//     // Build product_prices based on mode:
//     //  • Create: every valid line sent with product_id
//     //  • Edit:   existing rows sent with `id`; new rows with `product_id`
//     const items = editor.lines
//       .filter((l) => l.product_id && l.price_customer && l.price_agent)
//       .map((l) => {
//         if (editor.mode === "edit" && l.pre_order_product_id) {
//           return {
//             id: l.pre_order_product_id,
//             price_customer: l.price_customer,
//             price_agent: l.price_agent,
//             ...(l.stock_limit !== "" && {
//               stock_limit: Number(l.stock_limit),
//             }),
//           };
//         }
//         return {
//           product_id: l.product_id,
//           price_customer: l.price_customer,
//           price_agent: l.price_agent,
//           ...(l.stock_limit !== "" && {
//             stock_limit: Number(l.stock_limit),
//           }),
//         };
//       });

//     if (items.length > 0) payload.product_prices = items;

//     try {
//       const url =
//         editor.mode === "edit"
//           ? `/marketplace/pre-orders/${editor.campaignId}/`
//           : "/marketplace/pre-orders/";
//       const method = editor.mode === "edit" ? "PATCH" : "POST";

//       const res = await authFetch(url, {
//         method,
//         body: JSON.stringify(payload),
//       });
//       const data = await res.json();
//       if (!res.ok) throw new Error(parseApiError(data, "Could not save campaign"));

//       showToast(
//         editor.mode === "edit" ? "Campaign updated" : "Campaign opened",
//         "success"
//       );
//       closeEditor();
//       load({ silent: true });
//     } catch (err) {
//       setEditor((ed) => ({ ...ed, submitting: false }));
//       showToast(err.message, "error");
//     }
//   };

//   // ── Close campaign ─────────────────────────────────────
//   const closeCampaign = async (c) => {
//     if (
//       !window.confirm(
//         `Close "${c.name}"? Customers will no longer be able to join. This cannot be undone.`
//       )
//     )
//       return;
//     try {
//       const res = await authFetch(`/marketplace/pre-orders/${c.id}/`, {
//         method: "DELETE",
//       });
//       const data = await res.json().catch(() => ({}));
//       if (!res.ok) throw new Error(parseApiError(data, "Could not close campaign"));
//       showToast("Campaign closed", "success");
//       load({ silent: true });
//     } catch (err) {
//       showToast(err.message, "error");
//     }
//   };

//   // ── Render ──────────────────────────────────────────────
//   return (
//     <PageShell
//       eyebrow="Preorders"
//       title="Preorder campaigns"
//       subtitle="Open a timed window where customers order products together. All orders in a window ship once it closes."
//       actions={
//         <>
//           <Button
//             variant="secondary"
//             icon={RefreshCw}
//             onClick={() => load({ silent: true })}
//             disabled={refreshing}
//           >
//             Refresh
//           </Button>
//           <Button
//             icon={editor?.mode === "create" ? X : Plus}
//             onClick={editor?.mode === "create" ? closeEditor : openCreate}
//           >
//             {editor?.mode === "create" ? "Cancel" : "New campaign"}
//           </Button>
//         </>
//       }
//     >
//       {editor?.mode === "create" && (
//         <CampaignEditor
//           editor={editor}
//           setEditor={setEditor}
//           eligible={eligible}
//           productMap={productMap}
//           addLine={addLine}
//           removeLine={removeLine}
//           updateLine={updateLine}
//           onSubmit={submitEditor}
//           onCancel={closeEditor}
//           colors={t}
//         />
//       )}

//       <Section
//         title="All campaigns"
//         icon={CalendarDays}
//         chip={`${campaigns.length}`}
//       >
//         {loading ? (
//           <LoadingBlock label="Loading campaigns…" />
//         ) : campaigns.length === 0 ? (
//           <EmptyState
//             icon={CalendarDays}
//             title="No campaigns yet"
//             description="Open your first preorder window above. A window can include any number of pre-order products."
//           />
//         ) : (
//           <div style={{ display: "grid", gap: 10 }}>
//             {campaigns.map((c) => {
//               const isEditingThis =
//                 editor?.mode === "edit" && editor.campaignId === c.id;
//               if (isEditingThis) {
//                 return (
//                   <CampaignEditor
//                     key={c.id}
//                     editor={editor}
//                     setEditor={setEditor}
//                     eligible={eligible}
//                     productMap={productMap}
//                     addLine={addLine}
//                     removeLine={removeLine}
//                     updateLine={updateLine}
//                     onSubmit={submitEditor}
//                     onCancel={closeEditor}
//                     colors={t}
//                   />
//                 );
//               }
//               return (
//                 <CampaignCard
//                   key={c.id}
//                   campaign={c}
//                   productMap={productMap}
//                   colors={t}
//                   expanded={!!expanded[c.id]}
//                   onToggle={() =>
//                     setExpanded((prev) => ({ ...prev, [c.id]: !prev[c.id] }))
//                   }
//                   onClose={() => closeCampaign(c)}
//                   onEdit={() => openEdit(c)}
//                 />
//               );
//             })}
//           </div>
//         )}
//       </Section>
//     </PageShell>
//   );
// }

// /* ══════════════════════════════════════════════════════════
//    Editor (used for both create and edit)
//    ══════════════════════════════════════════════════════════ */

// const CampaignEditor = ({
//   editor,
//   setEditor,
//   eligible,
//   productMap,
//   addLine,
//   removeLine,
//   updateLine,
//   onSubmit,
//   onCancel,
//   colors,
// }) => {
//   const lineErrors = editor.errors._lines || {};
//   const isEdit = editor.mode === "edit";

//   const updateForm = (patch) =>
//     setEditor((ed) => ({ ...ed, form: { ...ed.form, ...patch } }));

//   // Products already used across all lines (for dropdown disabling)
//   const usedProductIds = useMemo(
//     () =>
//       new Set(
//         editor.lines
//           .map((l) => l.product_id)
//           .filter((id) => !!id)
//       ),
//     [editor.lines]
//   );

//   return (
//     <Section
//       title={isEdit ? "Edit campaign" : "Open a new campaign"}
//       description={
//         isEdit
//           ? "Update the window, adjust prices, or add new products. Existing orders are not affected."
//           : "Add one or more pre-order products. They will all share the same window."
//       }
//       icon={isEdit ? Pencil : CalendarDays}
//       chip={`${editor.lines.length} product${editor.lines.length === 1 ? "" : "s"}`}
//     >
//       <form
//         onSubmit={(e) => {
//           e.preventDefault();
//           onSubmit();
//         }}
//       >
//         <div style={styles.grid(3)}>
//           <div style={{ gridColumn: "1 / -1" }}>
//             <Field label="Campaign name" error={editor.errors.name}>
//               <Input
//                 value={editor.form.name}
//                 onChange={(e) => updateForm({ name: e.target.value })}
//                 placeholder="e.g. August group buy"
//               />
//             </Field>
//           </div>

//           <Field label="Starts" error={editor.errors.start_date}>
//             <Input
//               type="datetime-local"
//               value={editor.form.start_date}
//               onChange={(e) => updateForm({ start_date: e.target.value })}
//             />
//           </Field>
//           <Field label="Ends" error={editor.errors.end_date}>
//             <Input
//               type="datetime-local"
//               value={editor.form.end_date}
//               onChange={(e) => updateForm({ end_date: e.target.value })}
//             />
//           </Field>
//           <Field label="Status">
//             <Select
//               value={editor.form.is_active ? "active" : "draft"}
//               onChange={(e) =>
//                 updateForm({ is_active: e.target.value === "active" })
//               }
//             >
//               <option value="active">Active (open now)</option>
//               <option value="draft">Draft (hidden)</option>
//             </Select>
//           </Field>

//           <div style={{ gridColumn: "1 / -1" }}>
//             <Field label="Description" hint="Optional — shown to customers">
//               <Textarea
//                 value={editor.form.description}
//                 onChange={(e) => updateForm({ description: e.target.value })}
//                 placeholder="What is this window for? Shipping notes?"
//               />
//             </Field>
//           </div>
//         </div>

//         {/* Product lines */}
//         <div style={styles.linesHeader}>
//           <div style={styles.linesTitle(colors)}>
//             <Layers size={15} color={colors.accent} />
//             Products in this window
//           </div>
//           <Button size="sm" variant="secondary" icon={Plus} onClick={addLine}>
//             Add product
//           </Button>
//         </div>

//         {eligible.length === 0 ? (
//           <div style={styles.warnBox(colors)}>
//             <AlertCircle size={15} />
//             No pre-order products available. Set a product's sale type to
//             “Pre-order only” or “Both methods” in the catalogue first.
//           </div>
//         ) : editor.lines.length === 0 ? (
//           <div style={styles.warnBox(colors)}>
//             <AlertCircle size={15} />
//             Add at least one product to this campaign.
//           </div>
//         ) : (
//           <div style={{ display: "grid", gap: 10 }}>
//             {editor.lines.map((line, idx) => (
//               <ProductLine
//                 key={line.key}
//                 index={idx}
//                 line={line}
//                 errors={lineErrors[line.key] || {}}
//                 eligible={eligible}
//                 usedProductIds={usedProductIds}
//                 productMap={productMap}
//                 isEdit={isEdit}
//                 onChange={(patch) => updateLine(line.key, patch)}
//                 onRemove={() => removeLine(line.key)}
//                 colors={colors}
//               />
//             ))}
//           </div>
//         )}

//         {lineErrors._general && (
//           <div style={{ ...styles.warnBox(colors), marginTop: 12 }}>
//             <AlertCircle size={15} />
//             {lineErrors._general}
//           </div>
//         )}

//         <div style={styles.formActions(colors)}>
//           <Button variant="secondary" onClick={onCancel}>
//             Cancel
//           </Button>
//           <Button
//             type="submit"
//             icon={Save}
//             disabled={editor.submitting || eligible.length === 0}
//           >
//             {editor.submitting
//               ? isEdit
//                 ? "Saving…"
//                 : "Opening…"
//               : isEdit
//               ? "Save changes"
//               : "Open campaign"}
//           </Button>
//         </div>
//       </form>
//     </Section>
//   );
// };

// /* ══════════════════════════════════════════════════════════
//    Product line inside editor
//    ══════════════════════════════════════════════════════════ */

// const ProductLine = ({
//   index,
//   line,
//   errors,
//   eligible,
//   usedProductIds,
//   productMap,
//   isEdit,
//   onChange,
//   onRemove,
//   colors,
// }) => {
//   const productFromCatalog = line.product_id
//     ? productMap.get(line.product_id)
//     : null;
//   const displayName =
//     line.product_name || productFromCatalog?.name || "New product";
//   const isExisting = !!line.pre_order_product_id;

//   const customerNum = parseFloat(line.price_customer);
//   const agentNum = parseFloat(line.price_agent);
//   const profit =
//     !isNaN(customerNum) && !isNaN(agentNum) ? customerNum - agentNum : null;

//   const soldCount = line.sold_count || 0;

//   return (
//     <div style={styles.lineCard(colors)}>
//       <div style={styles.lineHeader}>
//         <div style={styles.lineIndex(colors)}>{index + 1}</div>
//         <div style={{ flex: 1, minWidth: 0 }}>
//           <div style={styles.lineTitle(colors)}>
//             {displayName}
//             {isEdit && isExisting && (
//               <span style={styles.lineBadge(colors)}>
//                 <Package size={10} />
//                 Existing
//               </span>
//             )}
//             {isEdit && !isExisting && line.product_id && (
//               <span style={{ ...styles.lineBadge(colors), color: colors.success, background: colors.successBg }}>
//                 New
//               </span>
//             )}
//           </div>
//           <div style={styles.lineSubtitle(colors)}>
//             {isExisting
//               ? `Sold so far: ${soldCount}`
//               : productFromCatalog?.sale_type === "BOTH"
//               ? "Ships both in-stock and pre-order"
//               : productFromCatalog?.sale_type === "PRE_ORDER"
//               ? "Pre-order only"
//               : "Pick a product to continue"}
//           </div>
//         </div>
//         <button
//           type="button"
//           onClick={onRemove}
//           style={styles.iconBtn(colors, true)}
//           aria-label={`Remove product ${index + 1}`}
//         >
//           <Trash2 size={14} />
//         </button>
//       </div>

//       <div style={styles.grid(4)}>
//         <div style={{ gridColumn: "1 / -1" }}>
//           <Field label="Product" error={errors.product_id}>
//             <Select
//               value={line.product_id}
//               disabled={isExisting}
//               onChange={(e) => {
//                 const pid = e.target.value;
//                 const p = eligible.find((x) => x.id === pid);
//                 onChange({
//                   product_id: pid,
//                   product_name: p?.name || "",
//                 });
//               }}
//             >
//               <option value="">Choose product</option>
//               {eligible.map((p) => {
//                 const taken =
//                   usedProductIds.has(p.id) && p.id !== line.product_id;
//                 return (
//                   <option key={p.id} value={p.id} disabled={taken}>
//                     {p.name}
//                     {taken ? "  (already added)" : ""}
//                   </option>
//                 );
//               })}
//             </Select>
//           </Field>
//         </div>

//         <Field label="Customer price (₵)" error={errors.price_customer}>
//           <Input
//             type="number"
//             min="0"
//             step="0.01"
//             value={line.price_customer}
//             onChange={(e) => onChange({ price_customer: e.target.value })}
//             placeholder="0.00"
//           />
//         </Field>

//         <Field label="Agent price (₵)" error={errors.price_agent}>
//           <Input
//             type="number"
//             min="0"
//             step="0.01"
//             value={line.price_agent}
//             onChange={(e) => onChange({ price_agent: e.target.value })}
//             placeholder="0.00"
//           />
//         </Field>

//         <Field
//           label="Stock limit"
//           hint="Empty = unlimited"
//           error={errors.stock_limit}
//         >
//           <Input
//             type="number"
//             min="0"
//             value={line.stock_limit}
//             onChange={(e) => onChange({ stock_limit: e.target.value })}
//             placeholder="Unlimited"
//           />
//         </Field>

//         <div
//           style={{
//             ...styles.profitTile(
//               colors,
//               profit != null && profit >= 0 ? "ok" : profit != null ? "bad" : "muted"
//             ),
//           }}
//         >
//           <div style={styles.profitLabel(colors)}>Profit per sale</div>
//           <div
//             style={{
//               fontSize: 15,
//               fontWeight: 800,
//               color:
//                 profit == null
//                   ? colors.muted
//                   : profit >= 0
//                   ? colors.success
//                   : colors.error,
//             }}
//           >
//             {profit == null ? "—" : `₵${profit.toFixed(2)}`}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// /* ══════════════════════════════════════════════════════════
//    Campaign card (display mode)
//    ══════════════════════════════════════════════════════════ */

// const CampaignCard = ({
//   campaign,
//   productMap,
//   colors,
//   expanded,
//   onToggle,
//   onClose,
//   onEdit,
// }) => {
//   const items = campaign.products || campaign.pre_order_products || [];
//   const total = campaign.total_products ?? items.length;
//   const phase = campaignPhase(campaign);

//   const canModify = phase !== "ended" && phase !== "closed";

//   const phaseStyle = {
//     open: { fg: colors.success, bg: colors.successBg, label: "Open" },
//     scheduled: { fg: colors.warning, bg: colors.warningBg, label: "Scheduled" },
//     ended: { fg: colors.muted, bg: colors.hoverBg, label: "Ended" },
//     closed: { fg: colors.error, bg: colors.errorBg, label: "Closed" },
//   }[phase] || { fg: colors.muted, bg: colors.hoverBg, label: "—" };

//   return (
//     <div style={styles.card(colors)}>
//       <div style={styles.cardTop}>
//         <div
//           style={{
//             ...styles.statusDot(colors),
//             background: phaseStyle.fg,
//             boxShadow: phase === "open" ? `0 0 0 4px ${phaseStyle.bg}` : "none",
//           }}
//         >
//           <Clock size={16} />
//         </div>

//         <div style={{ flex: 1, minWidth: 0 }}>
//           <div style={styles.cardTitle(colors)}>{campaign.name}</div>
//           <div style={styles.cardMeta(colors)}>
//             {fmtDateTime(campaign.start_date)} → {fmtDateTime(campaign.end_date)}
//             {" · "}
//             {total} product{total === 1 ? "" : "s"}
//           </div>
//         </div>

//         <span
//           style={{
//             ...styles.statusPill,
//             background: phaseStyle.bg,
//             color: phaseStyle.fg,
//           }}
//         >
//           {phaseStyle.label}
//         </span>

//         <button
//           type="button"
//           onClick={onToggle}
//           style={styles.iconBtn(colors)}
//           aria-label={expanded ? "Collapse" : "Expand"}
//         >
//           {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
//         </button>

//         {canModify && (
//           <>
//             <Button size="sm" variant="secondary" icon={Pencil} onClick={onEdit}>
//               Edit
//             </Button>
//             <Button
//               size="sm"
//               variant="dangerGhost"
//               icon={X}
//               onClick={onClose}
//             >
//               Close
//             </Button>
//           </>
//         )}
//       </div>

//       {expanded && (
//         <div style={styles.cardBody(colors)}>
//           {campaign.description && (
//             <p style={styles.cardDesc(colors)}>{campaign.description}</p>
//           )}

//           {items.length === 0 ? (
//             <div style={styles.emptyItems(colors)}>
//               <Package size={18} />
//               <span>No products in this campaign.</span>
//             </div>
//           ) : (
//             <div style={styles.itemList}>
//               {items.map((item) => {
//                 const cat = productMap.get(item.product);
//                 const img =
//                   item.product_images?.[0]?.image ||
//                   item.product_images?.[0]?.url ||
//                   cat?.thumbnail_image_url ||
//                   cat?.main_image_url ||
//                   null;
//                 const soldOut =
//                   item.stock_limit > 0 && item.sold_count >= item.stock_limit;
//                 return (
//                   <div key={item.id} style={styles.itemRow(colors)}>
//                     <div style={styles.itemThumb(colors)}>
//                       {img ? (
//                         <img
//                           src={img}
//                           alt=""
//                           style={{
//                             width: "100%",
//                             height: "100%",
//                             objectFit: "cover",
//                           }}
//                         />
//                       ) : (
//                         <Package size={16} />
//                       )}
//                     </div>
//                     <div style={{ flex: 1, minWidth: 0 }}>
//                       <div style={styles.itemName(colors)}>
//                         {item.product_name}
//                       </div>
//                       <div style={styles.itemMeta(colors)}>
//                         Customer ₵{Number(item.price_customer).toFixed(2)}
//                         {" · "}
//                         Agent ₵{Number(item.price_agent).toFixed(2)}
//                         {item.stock_limit > 0 && (
//                           <>
//                             {" · "}
//                             {item.sold_count}/{item.stock_limit} sold
//                           </>
//                         )}
//                       </div>
//                     </div>
//                     <span
//                       style={{
//                         ...styles.statusPill,
//                         background: soldOut
//                           ? colors.errorBg
//                           : item.is_available
//                           ? colors.successBg
//                           : colors.hoverBg,
//                         color: soldOut
//                           ? colors.error
//                           : item.is_available
//                           ? colors.success
//                           : colors.muted,
//                       }}
//                     >
//                       {soldOut
//                         ? "Sold out"
//                         : item.is_available
//                         ? "Available"
//                         : "Unavailable"}
//                     </span>
//                   </div>
//                 );
//               })}
//             </div>
//           )}
//         </div>
//       )}
//     </div>
//   );
// };

// /* ══════════════════════════════════════════════════════════
//    Styles
//    ══════════════════════════════════════════════════════════ */

// const styles = {
//   grid: (cols) => ({
//     display: "grid",
//     gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
//     gap: 12,
//     marginBottom: 14,
//   }),

//   linesHeader: {
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "space-between",
//     gap: 12,
//     marginTop: 8,
//     marginBottom: 12,
//     flexWrap: "wrap",
//   },
//   linesTitle: (c) => ({
//     display: "inline-flex",
//     alignItems: "center",
//     gap: 8,
//     fontSize: 13,
//     fontWeight: 700,
//     color: c.heading,
//   }),

//   lineCard: (c) => ({
//     border: `1px solid ${c.border}`,
//     borderRadius: 12,
//     padding: 14,
//     background: c.surfaceRaised || c.raisedBg,
//   }),
//   lineHeader: {
//     display: "flex",
//     alignItems: "center",
//     gap: 12,
//     marginBottom: 12,
//   },
//   lineIndex: (c) => ({
//     width: 26,
//     height: 26,
//     borderRadius: 8,
//     background: c.subtle,
//     color: c.accent,
//     display: "inline-flex",
//     alignItems: "center",
//     justifyContent: "center",
//     fontSize: 12,
//     fontWeight: 800,
//     flexShrink: 0,
//   }),
//   lineTitle: (c) => ({
//     display: "inline-flex",
//     alignItems: "center",
//     gap: 8,
//     fontSize: 13.5,
//     fontWeight: 700,
//     color: c.heading,
//     whiteSpace: "nowrap",
//     overflow: "hidden",
//     textOverflow: "ellipsis",
//   }),
//   lineBadge: (c) => ({
//     display: "inline-flex",
//     alignItems: "center",
//     gap: 3,
//     padding: "2px 7px",
//     borderRadius: 999,
//     background: c.subtle,
//     color: c.muted,
//     fontSize: 9.5,
//     fontWeight: 700,
//     letterSpacing: 0.3,
//     textTransform: "uppercase",
//   }),
//   lineSubtitle: (c) => ({
//     fontSize: 11.5,
//     color: c.muted,
//     marginTop: 2,
//   }),

//   profitTile: (c, kind) => ({
//     padding: 10,
//     borderRadius: 10,
//     background:
//       kind === "ok"
//         ? c.successBg
//         : kind === "bad"
//         ? c.errorBg
//         : c.subtle,
//     display: "flex",
//     flexDirection: "column",
//     gap: 2,
//     justifyContent: "center",
//   }),
//   profitLabel: (c) => ({
//     fontSize: 10.5,
//     fontWeight: 700,
//     letterSpacing: 0.3,
//     textTransform: "uppercase",
//     color: c.muted,
//   }),

//   warnBox: (c) => ({
//     display: "flex",
//     alignItems: "center",
//     gap: 8,
//     padding: "10px 12px",
//     borderRadius: 10,
//     background: c.warningBg,
//     color: c.warning,
//     fontSize: 12.5,
//     fontWeight: 600,
//   }),

//   formActions: (c) => ({
//     display: "flex",
//     justifyContent: "flex-end",
//     gap: 10,
//     marginTop: 16,
//     paddingTop: 14,
//     borderTop: `1px solid ${c.separator || c.border}`,
//   }),

//   card: (c) => ({
//     border: `1px solid ${c.border}`,
//     borderRadius: 12,
//     background: c.surfaceRaised || c.raisedBg,
//     overflow: "hidden",
//   }),
//   cardTop: {
//     display: "flex",
//     alignItems: "center",
//     gap: 12,
//     padding: 14,
//     flexWrap: "wrap",
//   },
//   statusDot: (c) => ({
//     width: 36,
//     height: 36,
//     borderRadius: 10,
//     color: "#fff",
//     display: "inline-flex",
//     alignItems: "center",
//     justifyContent: "center",
//     flexShrink: 0,
//     transition: "box-shadow 0.15s ease",
//   }),
//   cardTitle: (c) => ({
//     fontSize: 14,
//     fontWeight: 700,
//     color: c.heading,
//   }),
//   cardMeta: (c) => ({
//     fontSize: 11.5,
//     color: c.muted,
//     marginTop: 2,
//   }),
//   statusPill: {
//     fontSize: 11,
//     fontWeight: 700,
//     padding: "4px 10px",
//     borderRadius: 999,
//     textTransform: "uppercase",
//     letterSpacing: 0.2,
//     whiteSpace: "nowrap",
//   },

//   cardBody: (c) => ({
//     padding: 14,
//     borderTop: `1px solid ${c.separator || c.border}`,
//     background: c.surface || c.cardBg,
//   }),
//   cardDesc: (c) => ({
//     margin: "0 0 12px",
//     fontSize: 13,
//     color: c.text,
//     lineHeight: 1.55,
//   }),

//   emptyItems: (c) => ({
//     display: "flex",
//     alignItems: "center",
//     gap: 8,
//     padding: 14,
//     borderRadius: 10,
//     background: c.subtle,
//     color: c.muted,
//     fontSize: 12.5,
//   }),

//   itemList: { display: "grid", gap: 8 },
//   itemRow: (c) => ({
//     display: "flex",
//     alignItems: "center",
//     gap: 12,
//     padding: 10,
//     borderRadius: 10,
//     background: c.surfaceRaised || c.raisedBg,
//     border: `1px solid ${c.border}`,
//   }),
//   itemThumb: (c) => ({
//     width: 40,
//     height: 40,
//     borderRadius: 8,
//     background: c.subtle,
//     display: "inline-flex",
//     alignItems: "center",
//     justifyContent: "center",
//     overflow: "hidden",
//     flexShrink: 0,
//     color: c.muted,
//   }),
//   itemName: (c) => ({
//     fontSize: 13,
//     fontWeight: 700,
//     color: c.heading,
//     whiteSpace: "nowrap",
//     overflow: "hidden",
//     textOverflow: "ellipsis",
//   }),
//   itemMeta: (c) => ({
//     fontSize: 11.5,
//     color: c.muted,
//     marginTop: 2,
//   }),

//   iconBtn: (c, danger) => ({
//     width: 32,
//     height: 32,
//     borderRadius: 8,
//     border: `1px solid ${c.border}`,
//     background: "transparent",
//     color: danger ? c.error : c.muted,
//     cursor: "pointer",
//     display: "inline-flex",
//     alignItems: "center",
//     justifyContent: "center",
//     flexShrink: 0,
//   }),
// };



























// screens/marketplace/MarketplaceCampaigns.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  Plus,
  X,
  Clock,
  Trash2,
  Package,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  RefreshCw,
  Layers,
  Pencil,
  Save,
} from "lucide-react";
import { useAuth } from "../../providers/AuthProvider";
import { useToast } from "../../providers/ToastProvider";
import {
  PageShell,
  Section,
  Button,
  Field,
  Input,
  Textarea,
  Select,
  EmptyState,
  LoadingBlock,
  parseApiError,
  useTokens,
} from "./shared";
import { variantKeyLabel } from "./pricingApi";

/* ══════════════════════════════════════════════════════════
   Campaign API contract (see docs):
   POST   /marketplace/pre-orders/              → create with product_prices[]
   PATCH  /marketplace/pre-orders/{id}/         → merge:
       • item with `id`          → update existing PreOrderProduct
       • item with `product_id`  → add new product
       • omitted products        → untouched
   DELETE /marketplace/pre-orders/{id}/         → close (only while end_date > now)

   Variant awareness:
   • A product may have variants. In that case every product_prices entry
     MUST include a `variant_key` matching one of the product's variants.
   • A non-variant product's entry should NOT include `variant_key`
     (or send it as "" — the backend treats both as equivalent).
   • Each (product, variant_key) pair is a distinct PreOrderProduct row,
     so a campaign can include the same product at multiple variant scopes.

   Rules:
   • product_prices must contain ≥1 item on CREATE
   • start_date < end_date, start must be in the future on CREATE
   • product.sale_type must be PRE_ORDER or BOTH
   • price_customer, price_agent are decimals (no comparison rule)
   • stock_limit optional; 0 = unlimited
   • PATCH/DELETE rejected if end_date <= now
   ══════════════════════════════════════════════════════════ */

const emptyLine = () => ({
  key: Math.random().toString(36).slice(2),
  pre_order_product_id: null, // null → new; set → existing row
  product_id: "",
  variant_key: "", // "" for non-variant or product-level; else the key
  price_customer: "",
  price_agent: "",
  stock_limit: "",
  sold_count: 0,
  product_name: "",
});

const emptyForm = () => ({
  name: "",
  description: "",
  start_date: "",
  end_date: "",
  is_active: true,
});

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return (
    d.getFullYear() +
    "-" +
    pad(d.getMonth() + 1) +
    "-" +
    pad(d.getDate()) +
    "T" +
    pad(d.getHours()) +
    ":" +
    pad(d.getMinutes())
  );
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const campaignPhase = (c) => {
  const now = Date.now();
  const start = new Date(c.start_date).getTime();
  const end = new Date(c.end_date).getTime();
  if (!c.is_active) return "closed";
  if (now < start) return "scheduled";
  if (now > end) return "ended";
  return "open";
};

const productHasVariants = (product) =>
  !!product?.has_variants &&
  Array.isArray(product?.variants) &&
  product.variants.length > 0;

export default function MarketplaceCampaigns() {
  const { authFetch } = useAuth();
  const { showToast } = useToast();
  const t = useTokens();

  const [campaigns, setCampaigns] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Editor state — shared between create and edit
  const [editor, setEditor] = useState(null);

  const [expanded, setExpanded] = useState({});

  // ── Load ────────────────────────────────────────────────
  const load = useCallback(
    async ({ silent } = {}) => {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      try {
        const [cRes, pRes] = await Promise.all([
          authFetch("/marketplace/pre-orders/"),
          authFetch("/marketplace/products/"),
        ]);
        const [cData, pData] = await Promise.all([cRes.json(), pRes.json()]);
        if (!cRes.ok)
          throw new Error(parseApiError(cData, "Could not load campaigns"));
        if (!pRes.ok)
          throw new Error(parseApiError(pData, "Could not load products"));
        setCampaigns(cData.data || []);
        setProducts(pData.data || []);
      } catch (err) {
        showToast(err.message, "error");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [authFetch, showToast]
  );

  useEffect(() => {
    load();
  }, [load]);

  // ── Derived ─────────────────────────────────────────────
  const eligible = useMemo(
    () =>
      products.filter(
        (p) => p.sale_type === "PRE_ORDER" || p.sale_type === "BOTH"
      ),
    [products]
  );

  const productMap = useMemo(() => {
    const m = new Map();
    products.forEach((p) => m.set(p.id, p));
    return m;
  }, [products]);

  // ── Open editor: create ────────────────────────────────
  const openCreate = () => {
    setEditor({
      mode: "create",
      campaignId: null,
      form: emptyForm(),
      lines: [emptyLine()],
      submitting: false,
      errors: {},
    });
  };

  // ── Open editor: edit ──────────────────────────────────
  const openEdit = (campaign) => {
    const items = campaign.products || campaign.pre_order_products || [];
    setEditor({
      mode: "edit",
      campaignId: campaign.id,
      form: {
        name: campaign.name || "",
        description: campaign.description || "",
        start_date: toLocalInput(campaign.start_date),
        end_date: toLocalInput(campaign.end_date),
        is_active: !!campaign.is_active,
      },
      lines: items.map((it) => ({
        key: Math.random().toString(36).slice(2),
        pre_order_product_id: it.id,
        product_id: it.product,
        variant_key: it.variant_key || "",
        product_name: it.product_name,
        price_customer: String(it.price_customer || ""),
        price_agent: String(it.price_agent || ""),
        stock_limit:
          it.stock_limit === 0 || it.stock_limit == null
            ? ""
            : String(it.stock_limit),
        sold_count: it.sold_count || 0,
      })),
      submitting: false,
      errors: {},
    });
  };

  const closeEditor = () => setEditor(null);

  // ── Line mutations ─────────────────────────────────────
  const addLine = () =>
    setEditor((ed) =>
      ed ? { ...ed, lines: [...ed.lines, emptyLine()] } : ed
    );

  const removeLine = (key) =>
    setEditor((ed) =>
      ed ? { ...ed, lines: ed.lines.filter((l) => l.key !== key) } : ed
    );

  const updateLine = (key, patch) =>
    setEditor((ed) =>
      ed
        ? {
            ...ed,
            lines: ed.lines.map((l) => (l.key === key ? { ...l, ...patch } : l)),
          }
        : ed
    );

  // ── Validation ─────────────────────────────────────────
  const validate = (ed) => {
    const errs = {};
    if (!ed.form.name.trim()) errs.name = "Name is required";

    const start = ed.form.start_date ? new Date(ed.form.start_date) : null;
    const end = ed.form.end_date ? new Date(ed.form.end_date) : null;

    if (!start) errs.start_date = "Start is required";
    else if (ed.mode === "create" && start.getTime() < Date.now() - 60_000)
      errs.start_date = "Start cannot be in the past";
    if (!end) errs.end_date = "End is required";
    else if (start && end.getTime() <= start.getTime())
      errs.end_date = "End must be after start";

    const lineErrors = {};
    const seenScopes = new Set(); // key = `${product_id}:${variant_key || ""}`
    const validLines = ed.lines.filter(
      (l) => l.product_id && l.price_customer && l.price_agent
    );
    if (ed.mode === "create" && validLines.length === 0) {
      errs._lines = { _general: "Add at least one product with prices." };
    }

    ed.lines.forEach((l) => {
      const lerr = {};
      const hasAny =
        l.product_id ||
        l.price_customer ||
        l.price_agent ||
        l.stock_limit;
      if (!hasAny) return; // silently skip empty rows in edit mode

      if (!l.product_id) lerr.product_id = "Pick a product";
      else {
        const scope = `${l.product_id}:${l.variant_key || ""}`;
        if (seenScopes.has(scope))
          lerr.product_id = "Duplicate for this variant";
        else seenScopes.add(scope);

        // If the product has variants, a variant must be picked.
        const product = productMap.get(l.product_id);
        if (productHasVariants(product) && !l.variant_key) {
          lerr.variant_key = "Pick a variant";
        }
      }

      const c = parseFloat(l.price_customer);
      const a = parseFloat(l.price_agent);
      if (!l.price_customer || isNaN(c) || c < 0)
        lerr.price_customer = "Customer price required";
      if (!l.price_agent || isNaN(a) || a < 0)
        lerr.price_agent = "Agent price required";

      if (l.stock_limit !== "") {
        const n = Number(l.stock_limit);
        if (isNaN(n) || n < 0) lerr.stock_limit = "Invalid stock limit";
      }

      if (Object.keys(lerr).length) lineErrors[l.key] = lerr;
    });

    if (Object.keys(lineErrors).length) errs._lines = lineErrors;
    return errs;
  };

  // ── Submit (create or edit) ────────────────────────────
  const submitEditor = async () => {
    if (!editor) return;
    const errs = validate(editor);
    if (Object.keys(errs).length) {
      setEditor((ed) => ({ ...ed, errors: errs }));
      showToast("Please fix the highlighted fields", "error");
      return;
    }

    setEditor((ed) => ({ ...ed, submitting: true, errors: {} }));

    const form = editor.form;
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      start_date: new Date(form.start_date).toISOString(),
      end_date: new Date(form.end_date).toISOString(),
      is_active: form.is_active,
    };

    const items = editor.lines
      .filter((l) => l.product_id && l.price_customer && l.price_agent)
      .map((l) => {
        const product = productMap.get(l.product_id);
        const hasVariants = productHasVariants(product);
        const base = {
          price_customer: l.price_customer,
          price_agent: l.price_agent,
          ...(l.stock_limit !== "" && {
            stock_limit: Number(l.stock_limit),
          }),
        };
        // Only include variant_key when the product actually has variants.
        if (hasVariants && l.variant_key) base.variant_key = l.variant_key;

        if (editor.mode === "edit" && l.pre_order_product_id) {
          return { id: l.pre_order_product_id, ...base };
        }
        return { product_id: l.product_id, ...base };
      });

    if (items.length > 0) payload.product_prices = items;

    try {
      const url =
        editor.mode === "edit"
          ? `/marketplace/pre-orders/${editor.campaignId}/`
          : "/marketplace/pre-orders/";
      const method = editor.mode === "edit" ? "PATCH" : "POST";

      const res = await authFetch(url, {
        method,
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok)
        throw new Error(parseApiError(data, "Could not save campaign"));

      showToast(
        editor.mode === "edit" ? "Campaign updated" : "Campaign opened",
        "success"
      );
      closeEditor();
      load({ silent: true });
    } catch (err) {
      setEditor((ed) => ({ ...ed, submitting: false }));
      showToast(err.message, "error");
    }
  };

  // ── Close campaign ─────────────────────────────────────
  const closeCampaign = async (c) => {
    if (
      !window.confirm(
        `Close "${c.name}"? Customers will no longer be able to join. This cannot be undone.`
      )
    )
      return;
    try {
      const res = await authFetch(`/marketplace/pre-orders/${c.id}/`, {
        method: "DELETE",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok)
        throw new Error(parseApiError(data, "Could not close campaign"));
      showToast("Campaign closed", "success");
      load({ silent: true });
    } catch (err) {
      showToast(err.message, "error");
    }
  };

  // ── Render ──────────────────────────────────────────────
  return (
    <PageShell
      eyebrow="Preorders"
      title="Preorder campaigns"
      subtitle="Open a timed window where customers order products together. All orders in a window ship once it closes."
      actions={
        <>
          <Button
            variant="secondary"
            icon={RefreshCw}
            onClick={() => load({ silent: true })}
            disabled={refreshing}
          >
            Refresh
          </Button>
          <Button
            icon={editor?.mode === "create" ? X : Plus}
            onClick={editor?.mode === "create" ? closeEditor : openCreate}
          >
            {editor?.mode === "create" ? "Cancel" : "New campaign"}
          </Button>
        </>
      }
    >
      {editor?.mode === "create" && (
        <CampaignEditor
          editor={editor}
          setEditor={setEditor}
          eligible={eligible}
          productMap={productMap}
          addLine={addLine}
          removeLine={removeLine}
          updateLine={updateLine}
          onSubmit={submitEditor}
          onCancel={closeEditor}
          colors={t}
        />
      )}

      <Section
        title="All campaigns"
        icon={CalendarDays}
        chip={`${campaigns.length}`}
      >
        {loading ? (
          <LoadingBlock label="Loading campaigns…" />
        ) : campaigns.length === 0 ? (
          <EmptyState
            icon={CalendarDays}
            title="No campaigns yet"
            description="Open your first preorder window above. A window can include any number of pre-order products."
          />
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {campaigns.map((c) => {
              const isEditingThis =
                editor?.mode === "edit" && editor.campaignId === c.id;
              if (isEditingThis) {
                return (
                  <CampaignEditor
                    key={c.id}
                    editor={editor}
                    setEditor={setEditor}
                    eligible={eligible}
                    productMap={productMap}
                    addLine={addLine}
                    removeLine={removeLine}
                    updateLine={updateLine}
                    onSubmit={submitEditor}
                    onCancel={closeEditor}
                    colors={t}
                  />
                );
              }
              return (
                <CampaignCard
                  key={c.id}
                  campaign={c}
                  productMap={productMap}
                  colors={t}
                  expanded={!!expanded[c.id]}
                  onToggle={() =>
                    setExpanded((prev) => ({ ...prev, [c.id]: !prev[c.id] }))
                  }
                  onClose={() => closeCampaign(c)}
                  onEdit={() => openEdit(c)}
                />
              );
            })}
          </div>
        )}
      </Section>
    </PageShell>
  );
}

/* ══════════════════════════════════════════════════════════
   Editor (used for both create and edit)
   ══════════════════════════════════════════════════════════ */

const CampaignEditor = ({
  editor,
  setEditor,
  eligible,
  productMap,
  addLine,
  removeLine,
  updateLine,
  onSubmit,
  onCancel,
  colors,
}) => {
  const lineErrors = editor.errors._lines || {};
  const isEdit = editor.mode === "edit";

  const updateForm = (patch) =>
    setEditor((ed) => ({ ...ed, form: { ...ed.form, ...patch } }));

  // Scope keys already used across lines: `${product_id}:${variant_key || ""}`
  const usedScopes = useMemo(
    () =>
      new Set(
        editor.lines
          .filter((l) => l.product_id)
          .map((l) => `${l.product_id}:${l.variant_key || ""}`)
      ),
    [editor.lines]
  );

  return (
    <Section
      title={isEdit ? "Edit campaign" : "Open a new campaign"}
      description={
        isEdit
          ? "Update the window, adjust prices, or add new products. Existing orders are not affected."
          : "Add one or more pre-order products. They will all share the same window."
      }
      icon={isEdit ? Pencil : CalendarDays}
      chip={`${editor.lines.length} product${editor.lines.length === 1 ? "" : "s"}`}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <div style={styles.grid(3)}>
          <div style={{ gridColumn: "1 / -1" }}>
            <Field label="Campaign name" error={editor.errors.name}>
              <Input
                value={editor.form.name}
                onChange={(e) => updateForm({ name: e.target.value })}
                placeholder="e.g. August group buy"
              />
            </Field>
          </div>

          <Field label="Starts" error={editor.errors.start_date}>
            <Input
              type="datetime-local"
              value={editor.form.start_date}
              onChange={(e) => updateForm({ start_date: e.target.value })}
            />
          </Field>
          <Field label="Ends" error={editor.errors.end_date}>
            <Input
              type="datetime-local"
              value={editor.form.end_date}
              onChange={(e) => updateForm({ end_date: e.target.value })}
            />
          </Field>
          <Field label="Status">
            <Select
              value={editor.form.is_active ? "active" : "draft"}
              onChange={(e) =>
                updateForm({ is_active: e.target.value === "active" })
              }
            >
              <option value="active">Active (open now)</option>
              <option value="draft">Draft (hidden)</option>
            </Select>
          </Field>

          <div style={{ gridColumn: "1 / -1" }}>
            <Field label="Description" hint="Optional — shown to customers">
              <Textarea
                value={editor.form.description}
                onChange={(e) => updateForm({ description: e.target.value })}
                placeholder="What is this window for? Shipping notes?"
              />
            </Field>
          </div>
        </div>

        {/* Product lines */}
        <div style={styles.linesHeader}>
          <div style={styles.linesTitle(colors)}>
            <Layers size={15} color={colors.accent} />
            Products in this window
          </div>
          <Button size="sm" variant="secondary" icon={Plus} onClick={addLine}>
            Add product
          </Button>
        </div>

        {eligible.length === 0 ? (
          <div style={styles.warnBox(colors)}>
            <AlertCircle size={15} />
            No pre-order products available. Set a product's sale type to
            “Pre-order only” or “Both methods” in the catalogue first.
          </div>
        ) : editor.lines.length === 0 ? (
          <div style={styles.warnBox(colors)}>
            <AlertCircle size={15} />
            Add at least one product to this campaign.
          </div>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {editor.lines.map((line, idx) => (
              <ProductLine
                key={line.key}
                index={idx}
                line={line}
                errors={lineErrors[line.key] || {}}
                eligible={eligible}
                usedScopes={usedScopes}
                productMap={productMap}
                isEdit={isEdit}
                onChange={(patch) => updateLine(line.key, patch)}
                onRemove={() => removeLine(line.key)}
                colors={colors}
              />
            ))}
          </div>
        )}

        {lineErrors._general && (
          <div style={{ ...styles.warnBox(colors), marginTop: 12 }}>
            <AlertCircle size={15} />
            {lineErrors._general}
          </div>
        )}

        <div style={styles.formActions(colors)}>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            icon={Save}
            disabled={editor.submitting || eligible.length === 0}
          >
            {editor.submitting
              ? isEdit
                ? "Saving…"
                : "Opening…"
              : isEdit
              ? "Save changes"
              : "Open campaign"}
          </Button>
        </div>
      </form>
    </Section>
  );
};

/* ══════════════════════════════════════════════════════════
   Product line inside editor
   ══════════════════════════════════════════════════════════ */

const ProductLine = ({
  index,
  line,
  errors,
  eligible,
  usedScopes,
  productMap,
  isEdit,
  onChange,
  onRemove,
  colors,
}) => {
  const productFromCatalog = line.product_id
    ? productMap.get(line.product_id)
    : null;
  const hasVariants = productHasVariants(productFromCatalog);
  const isExisting = !!line.pre_order_product_id;

  // Friendly display name — append the variant label if one is picked.
  const variantSuffix = line.variant_key
    ? ` · ${variantKeyLabel(line.variant_key)}`
    : hasVariants
    ? " · All variants"
    : "";
  const displayName =
    line.product_name || productFromCatalog?.name || "New product";

  const customerNum = parseFloat(line.price_customer);
  const agentNum = parseFloat(line.price_agent);
  const profit =
    !isNaN(customerNum) && !isNaN(agentNum) ? customerNum - agentNum : null;

  const soldCount = line.sold_count || 0;

  return (
    <div style={styles.lineCard(colors)}>
      <div style={styles.lineHeader}>
        <div style={styles.lineIndex(colors)}>{index + 1}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={styles.lineTitle(colors)}>
            {displayName}
            {hasVariants && (
              <span style={styles.lineVariantTag(colors)}>
                <Layers size={10} />
                {line.variant_key
                  ? variantKeyLabel(line.variant_key)
                  : "All variants"}
              </span>
            )}
            {isEdit && isExisting && (
              <span style={styles.lineBadge(colors)}>
                <Package size={10} />
                Existing
              </span>
            )}
            {isEdit && !isExisting && line.product_id && (
              <span
                style={{
                  ...styles.lineBadge(colors),
                  color: colors.success,
                  background: colors.successBg,
                }}
              >
                New
              </span>
            )}
          </div>
          <div style={styles.lineSubtitle(colors)}>
            {isExisting
              ? `Sold so far: ${soldCount}`
              : productFromCatalog?.sale_type === "BOTH"
              ? "Ships both in-stock and pre-order"
              : productFromCatalog?.sale_type === "PRE_ORDER"
              ? "Pre-order only"
              : "Pick a product to continue"}
          </div>
        </div>
        <button
          type="button"
          onClick={onRemove}
          style={styles.iconBtn(colors, true)}
          aria-label={`Remove product ${index + 1}`}
        >
          <Trash2 size={14} />
        </button>
      </div>

      <div style={styles.grid(4)}>
        <div style={{ gridColumn: "1 / -1" }}>
          <Field label="Product" error={errors.product_id}>
            <Select
              value={line.product_id}
              disabled={isExisting}
              onChange={(e) => {
                const pid = e.target.value;
                const p = eligible.find((x) => x.id === pid);
                onChange({
                  product_id: pid,
                  product_name: p?.name || "",
                  // Reset variant when the product changes.
                  variant_key: "",
                });
              }}
            >
              <option value="">Choose product</option>
              {eligible.map((p) => {
                // A product is "taken" only when this exact line doesn't
                // already use it. Same product, different variant → allowed.
                const taken =
                  usedScopes.has(`${p.id}:${line.variant_key || ""}`) &&
                  p.id !== line.product_id;
                const hasVar = productHasVariants(p);
                return (
                  <option key={p.id} value={p.id} disabled={taken}>
                    {p.name}
                    {hasVar ? " (variants)" : ""}
                    {taken ? "  (already added)" : ""}
                  </option>
                );
              })}
            </Select>
          </Field>
        </div>

        {/* Variant picker — only shown when the selected product has variants */}
        {hasVariants && (
          <div style={{ gridColumn: "1 / -1" }}>
            <Field label="Variant" error={errors.variant_key}>
              <Select
                value={line.variant_key}
                disabled={isExisting}
                onChange={(e) =>
                  onChange({ variant_key: e.target.value })
                }
              >
                <option value="">Choose variant</option>
                {(productFromCatalog.variants || []).map((v) => {
                  const taken =
                    usedScopes.has(`${line.product_id}:${v.key}`) &&
                    v.key !== line.variant_key;
                  return (
                    <option key={v.key} value={v.key} disabled={taken}>
                      {variantKeyLabel(v.key)}
                      {taken ? "  (already added)" : ""}
                    </option>
                  );
                })}
              </Select>
            </Field>
          </div>
        )}

        <Field label="Customer price (₵)" error={errors.price_customer}>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={line.price_customer}
            onChange={(e) => onChange({ price_customer: e.target.value })}
            placeholder="0.00"
          />
        </Field>

        <Field label="Agent price (₵)" error={errors.price_agent}>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={line.price_agent}
            onChange={(e) => onChange({ price_agent: e.target.value })}
            placeholder="0.00"
          />
        </Field>

        <Field
          label="Stock limit"
          hint="Empty = unlimited"
          error={errors.stock_limit}
        >
          <Input
            type="number"
            min="0"
            value={line.stock_limit}
            onChange={(e) => onChange({ stock_limit: e.target.value })}
            placeholder="Unlimited"
          />
        </Field>

        <div
          style={{
            ...styles.profitTile(
              colors,
              profit != null && profit >= 0 ? "ok" : profit != null ? "bad" : "muted"
            ),
          }}
        >
          <div style={styles.profitLabel(colors)}>Profit per sale</div>
          <div
            style={{
              fontSize: 15,
              fontWeight: 800,
              color:
                profit == null
                  ? colors.muted
                  : profit >= 0
                  ? colors.success
                  : colors.error,
            }}
          >
            {profit == null ? "—" : `₵${profit.toFixed(2)}`}
          </div>
        </div>
      </div>

      {/* Quiet summary line — shows the display name with variant suffix
          when a variant is picked but the product doesn't have variants.
          Guards against the edge case where a product lost its variants. */}
      {!hasVariants && variantSuffix && (
        <div style={styles.lineSuffix(colors)}>{variantSuffix.trim()}</div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   Campaign card (display mode)
   ══════════════════════════════════════════════════════════ */

const CampaignCard = ({
  campaign,
  productMap,
  colors,
  expanded,
  onToggle,
  onClose,
  onEdit,
}) => {
  const items = campaign.products || campaign.pre_order_products || [];
  const total = campaign.total_products ?? items.length;
  const phase = campaignPhase(campaign);

  const canModify = phase !== "ended" && phase !== "closed";

  const phaseStyle = {
    open: { fg: colors.success, bg: colors.successBg, label: "Open" },
    scheduled: { fg: colors.warning, bg: colors.warningBg, label: "Scheduled" },
    ended: { fg: colors.muted, bg: colors.hoverBg, label: "Ended" },
    closed: { fg: colors.error, bg: colors.errorBg, label: "Closed" },
  }[phase] || { fg: colors.muted, bg: colors.hoverBg, label: "—" };

  return (
    <div style={styles.card(colors)}>
      <div style={styles.cardTop}>
        <div
          style={{
            ...styles.statusDot(colors),
            background: phaseStyle.fg,
            boxShadow: phase === "open" ? `0 0 0 4px ${phaseStyle.bg}` : "none",
          }}
        >
          <Clock size={16} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={styles.cardTitle(colors)}>{campaign.name}</div>
          <div style={styles.cardMeta(colors)}>
            {fmtDateTime(campaign.start_date)} → {fmtDateTime(campaign.end_date)}
            {" · "}
            {total} product{total === 1 ? "" : "s"}
          </div>
        </div>

        <span
          style={{
            ...styles.statusPill,
            background: phaseStyle.bg,
            color: phaseStyle.fg,
          }}
        >
          {phaseStyle.label}
        </span>

        <button
          type="button"
          onClick={onToggle}
          style={styles.iconBtn(colors)}
          aria-label={expanded ? "Collapse" : "Expand"}
        >
          {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </button>

        {canModify && (
          <>
            <Button size="sm" variant="secondary" icon={Pencil} onClick={onEdit}>
              Edit
            </Button>
            <Button
              size="sm"
              variant="dangerGhost"
              icon={X}
              onClick={onClose}
            >
              Close
            </Button>
          </>
        )}
      </div>

      {expanded && (
        <div style={styles.cardBody(colors)}>
          {campaign.description && (
            <p style={styles.cardDesc(colors)}>{campaign.description}</p>
          )}

          {items.length === 0 ? (
            <div style={styles.emptyItems(colors)}>
              <Package size={18} />
              <span>No products in this campaign.</span>
            </div>
          ) : (
            <div style={styles.itemList}>
              {items.map((item) => {
                const cat = productMap.get(item.product);
                const img =
                  item.product_images?.[0]?.image ||
                  item.product_images?.[0]?.url ||
                  cat?.thumbnail_image_url ||
                  cat?.main_image_url ||
                  null;
                const soldOut =
                  item.stock_limit > 0 && item.sold_count >= item.stock_limit;
                const hasVariant = !!item.variant_key;
                return (
                  <div key={item.id} style={styles.itemRow(colors)}>
                    <div style={styles.itemThumb(colors)}>
                      {img ? (
                        <img
                          src={img}
                          alt=""
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <Package size={16} />
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={styles.itemName(colors)}>
                        {item.product_name}
                        {hasVariant && (
                          <span style={styles.itemVariantTag(colors)}>
                            <Layers size={9} />
                            {variantKeyLabel(item.variant_key)}
                          </span>
                        )}
                      </div>
                      <div style={styles.itemMeta(colors)}>
                        Customer ₵{Number(item.price_customer).toFixed(2)}
                        {" · "}
                        Agent ₵{Number(item.price_agent).toFixed(2)}
                        {item.stock_limit > 0 && (
                          <>
                            {" · "}
                            {item.sold_count}/{item.stock_limit} sold
                          </>
                        )}
                      </div>
                    </div>
                    <span
                      style={{
                        ...styles.statusPill,
                        background: soldOut
                          ? colors.errorBg
                          : item.is_available
                          ? colors.successBg
                          : colors.hoverBg,
                        color: soldOut
                          ? colors.error
                          : item.is_available
                          ? colors.success
                          : colors.muted,
                      }}
                    >
                      {soldOut
                        ? "Sold out"
                        : item.is_available
                        ? "Available"
                        : "Unavailable"}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   Styles
   ══════════════════════════════════════════════════════════ */

const styles = {
  grid: (cols) => ({
    display: "grid",
    gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
    gap: 12,
    marginBottom: 14,
  }),

  linesHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginTop: 8,
    marginBottom: 12,
    flexWrap: "wrap",
  },
  linesTitle: (c) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    fontWeight: 700,
    color: c.heading,
  }),

  lineCard: (c) => ({
    border: `1px solid ${c.border}`,
    borderRadius: 12,
    padding: 14,
    background: c.surfaceRaised || c.raisedBg,
  }),
  lineHeader: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 12,
  },
  lineIndex: (c) => ({
    width: 26,
    height: 26,
    borderRadius: 8,
    background: c.subtle,
    color: c.accent,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 800,
    flexShrink: 0,
  }),
  lineTitle: (c) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13.5,
    fontWeight: 700,
    color: c.heading,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    flexWrap: "wrap",
  }),
  lineVariantTag: (c) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    padding: "2px 7px",
    borderRadius: 999,
    background: `${c.accent}18`,
    color: c.accent,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.2,
  }),
  lineBadge: (c) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    padding: "2px 7px",
    borderRadius: 999,
    background: c.subtle,
    color: c.muted,
    fontSize: 9.5,
    fontWeight: 700,
    letterSpacing: 0.3,
    textTransform: "uppercase",
  }),
  lineSubtitle: (c) => ({
    fontSize: 11.5,
    color: c.muted,
    marginTop: 2,
  }),
  lineSuffix: (c) => ({
    marginTop: 8,
    fontSize: 11,
    color: c.muted,
    fontStyle: "italic",
  }),

  profitTile: (c, kind) => ({
    padding: 10,
    borderRadius: 10,
    background:
      kind === "ok"
        ? c.successBg
        : kind === "bad"
        ? c.errorBg
        : c.subtle,
    display: "flex",
    flexDirection: "column",
    gap: 2,
    justifyContent: "center",
  }),
  profitLabel: (c) => ({
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: 0.3,
    textTransform: "uppercase",
    color: c.muted,
  }),

  warnBox: (c) => ({
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 12px",
    borderRadius: 10,
    background: c.warningBg,
    color: c.warning,
    fontSize: 12.5,
    fontWeight: 600,
  }),

  formActions: (c) => ({
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 16,
    paddingTop: 14,
    borderTop: `1px solid ${c.separator || c.border}`,
  }),

  card: (c) => ({
    border: `1px solid ${c.border}`,
    borderRadius: 12,
    background: c.surfaceRaised || c.raisedBg,
    overflow: "hidden",
  }),
  cardTop: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: 14,
    flexWrap: "wrap",
  },
  statusDot: (c) => ({
    width: 36,
    height: 36,
    borderRadius: 10,
    color: "#fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "box-shadow 0.15s ease",
  }),
  cardTitle: (c) => ({
    fontSize: 14,
    fontWeight: 700,
    color: c.heading,
  }),
  cardMeta: (c) => ({
    fontSize: 11.5,
    color: c.muted,
    marginTop: 2,
  }),
  statusPill: {
    fontSize: 11,
    fontWeight: 700,
    padding: "4px 10px",
    borderRadius: 999,
    textTransform: "uppercase",
    letterSpacing: 0.2,
    whiteSpace: "nowrap",
  },

  cardBody: (c) => ({
    padding: 14,
    borderTop: `1px solid ${c.separator || c.border}`,
    background: c.surface || c.cardBg,
  }),
  cardDesc: (c) => ({
    margin: "0 0 12px",
    fontSize: 13,
    color: c.text,
    lineHeight: 1.55,
  }),

  emptyItems: (c) => ({
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: 14,
    borderRadius: 10,
    background: c.subtle,
    color: c.muted,
    fontSize: 12.5,
  }),

  itemList: { display: "grid", gap: 8 },
  itemRow: (c) => ({
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: 10,
    borderRadius: 10,
    background: c.surfaceRaised || c.raisedBg,
    border: `1px solid ${c.border}`,
  }),
  itemThumb: (c) => ({
    width: 40,
    height: 40,
    borderRadius: 8,
    background: c.subtle,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    flexShrink: 0,
    color: c.muted,
  }),
  itemName: (c) => ({
    fontSize: 13,
    fontWeight: 700,
    color: c.heading,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    minWidth: 0,
  }),
  itemVariantTag: (c) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    padding: "1px 7px",
    borderRadius: 999,
    background: `${c.accent}18`,
    color: c.accent,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.2,
  }),
  itemMeta: (c) => ({
    fontSize: 11.5,
    color: c.muted,
    marginTop: 2,
  }),

  iconBtn: (c, danger) => ({
    width: 32,
    height: 32,
    borderRadius: 8,
    border: `1px solid ${c.border}`,
    background: "transparent",
    color: danger ? c.error : c.muted,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),
};